import { useState, useEffect, useMemo } from 'react';
import { format } from 'date-fns';
import { FileText, CheckCircle, Circle, Send, Save, Copy, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useAppealTemplates, useAppeals } from '@/hooks/useAppealTemplates';
import { generateAppealContent, type AppealPlaceholders } from '@/lib/normalization/appealTemplates';
import { DENIAL_CATEGORY_LABELS, type DenialCategory } from '@/lib/normalization/denialNormalization';
import { DenialCategoryBadge } from './DenialCategoryBadge';
import type { AuthorizationRequest, Patient, Payer, Facility } from '@/lib/authorization/types';

interface AppealBuilderProps {
  authorization: AuthorizationRequest;
  patient?: Patient;
  payer?: Payer;
  facility?: Facility;
  surgeonName?: string;
  onAppealCreated?: () => void;
}

const DENIAL_CATEGORIES: DenialCategory[] = [
  'MEDICAL_NECESSITY',
  'DOCUMENTATION_MISSING',
  'CODING_MODIFIER',
  'COVERAGE_LIMITATION',
  'TIMELY_FILING',
  'NON_COVERED_SERVICE',
  'OTHER',
];

export function AppealBuilder({
  authorization,
  patient,
  payer,
  facility,
  surgeonName,
  onAppealCreated,
}: AppealBuilderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<DenialCategory>(
    (authorization.denial_category as DenialCategory) || 'MEDICAL_NECESSITY'
  );
  const [appealContent, setAppealContent] = useState('');
  const [clinicalRationale, setClinicalRationale] = useState('');
  const [completedChecklist, setCompletedChecklist] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { getTemplateForCategory, loading: templatesLoading } = useAppealTemplates({
    payerId: authorization.payer_id,
    denialCategory: selectedCategory,
  });

  const { createAppeal, submitAppeal } = useAppeals(authorization.id);

  const currentTemplate = useMemo(() => 
    getTemplateForCategory(selectedCategory, authorization.payer_id),
    [selectedCategory, authorization.payer_id, getTemplateForCategory]
  );

  // Build placeholders from authorization data
  const placeholders: Partial<AppealPlaceholders> = useMemo(() => ({
    patientInitials: patient ? `${patient.first_name[0]}${patient.last_name[0]}` : '',
    authorizationId: authorization.id,
    caseNumber: authorization.case_number,
    dateOfService: authorization.scheduled_date 
      ? format(new Date(authorization.scheduled_date), 'MM/dd/yyyy') 
      : '',
    cptCodes: authorization.cpt_codes?.join(', ') || '',
    modifiers: authorization.modifiers?.join(', ') || '',
    diagnosisCodes: authorization.icd10_codes?.join(', ') || '',
    clinicalRationale: clinicalRationale || authorization.diagnosis_summary || '',
    attachedEvidence: completedChecklist,
    payerName: payer?.name || '',
    denialDate: authorization.payer_response_date 
      ? format(new Date(authorization.payer_response_date), 'MM/dd/yyyy')
      : '',
    denialReason: authorization.denial_reason || authorization.denial_raw_text || '',
    surgeonName: surgeonName || '',
    facilityName: facility?.name || '',
  }), [authorization, patient, payer, facility, surgeonName, clinicalRationale, completedChecklist]);

  // Generate appeal content when template or placeholders change
  useEffect(() => {
    if (currentTemplate) {
      const generated = generateAppealContent(currentTemplate.templateContent, placeholders);
      setAppealContent(generated);
    }
  }, [currentTemplate, placeholders]);

  const handleChecklistToggle = (item: string) => {
    setCompletedChecklist(prev =>
      prev.includes(item)
        ? prev.filter(i => i !== item)
        : [...prev, item]
    );
  };

  const handleRegenerateContent = () => {
    if (currentTemplate) {
      const generated = generateAppealContent(currentTemplate.templateContent, placeholders);
      setAppealContent(generated);
      toast.success('Appeal content regenerated');
    }
  };

  const handleCopyToClipboard = async () => {
    await navigator.clipboard.writeText(appealContent);
    toast.success('Appeal copied to clipboard');
  };

  const handleSaveDraft = async () => {
    setIsSubmitting(true);
    try {
      await createAppeal(currentTemplate?.id || null, selectedCategory, appealContent);
      toast.success('Appeal draft saved');
      onAppealCreated?.();
    } catch (err) {
      toast.error('Failed to save appeal draft');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitAppeal = async () => {
    setIsSubmitting(true);
    try {
      const appeal = await createAppeal(currentTemplate?.id || null, selectedCategory, appealContent);
      if (appeal) {
        await submitAppeal(appeal.id);
        toast.success('Appeal submitted');
        setIsOpen(false);
        onAppealCreated?.();
      }
    } catch (err) {
      toast.error('Failed to submit appeal');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2">
          <FileText className="h-4 w-4" />
          Build Appeal
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0">
        <DialogHeader className="p-6 pb-0">
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Appeal Builder - {authorization.case_number}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-4 p-6 pt-4">
          {/* Left column - Configuration */}
          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Denial Category</CardTitle>
              </CardHeader>
              <CardContent>
                <Select 
                  value={selectedCategory} 
                  onValueChange={(v) => setSelectedCategory(v as DenialCategory)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DENIAL_CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>
                        {DENIAL_CATEGORY_LABELS[cat]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {authorization.denial_category && (
                  <div className="mt-2">
                    <p className="text-xs text-muted-foreground mb-1">Auto-classified:</p>
                    <DenialCategoryBadge 
                      category={authorization.denial_category as DenialCategory}
                      confidence={(authorization.denial_category_confidence as any) || 'TEMPLATE'}
                      rawText={authorization.denial_raw_text}
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Clinical Rationale</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="Enter the clinical rationale for appeal..."
                  value={clinicalRationale}
                  onChange={(e) => setClinicalRationale(e.target.value)}
                  rows={4}
                  className="text-sm"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Required Documentation</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {currentTemplate?.checklistItems.map((item, idx) => (
                    <label
                      key={idx}
                      className="flex items-start gap-2 text-sm cursor-pointer"
                    >
                      <Checkbox
                        checked={completedChecklist.includes(item)}
                        onCheckedChange={() => handleChecklistToggle(item)}
                        className="mt-0.5"
                      />
                      <span className={cn(
                        completedChecklist.includes(item) && "line-through text-muted-foreground"
                      )}>
                        {item}
                      </span>
                    </label>
                  ))}
                </div>
                <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                  {completedChecklist.length === currentTemplate?.checklistItems.length ? (
                    <CheckCircle className="h-3 w-3 text-emerald-500" />
                  ) : (
                    <Circle className="h-3 w-3" />
                  )}
                  {completedChecklist.length} / {currentTemplate?.checklistItems.length || 0} complete
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Template Info</CardTitle>
              </CardHeader>
              <CardContent className="text-xs text-muted-foreground space-y-1">
                <p><span className="font-medium">Template:</span> {currentTemplate?.templateName}</p>
                <p><span className="font-medium">Version:</span> {currentTemplate?.version}</p>
                <p><span className="font-medium">Payer-specific:</span> {currentTemplate?.payerId ? 'Yes' : 'No'}</p>
              </CardContent>
            </Card>
          </div>

          {/* Right column - Appeal content */}
          <div className="col-span-2 flex flex-col">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-medium">Appeal Letter</Label>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRegenerateContent}
                  className="h-7 text-xs"
                >
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Regenerate
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCopyToClipboard}
                  className="h-7 text-xs"
                >
                  <Copy className="h-3 w-3 mr-1" />
                  Copy
                </Button>
              </div>
            </div>
            <ScrollArea className="flex-1 border rounded-md">
              <Textarea
                value={appealContent}
                onChange={(e) => setAppealContent(e.target.value)}
                className="min-h-[400px] border-0 resize-none font-mono text-sm"
                placeholder="Appeal content will be generated here..."
              />
            </ScrollArea>

            <div className="flex items-center justify-end gap-2 mt-4">
              <Button
                variant="outline"
                onClick={handleSaveDraft}
                disabled={isSubmitting || !appealContent}
              >
                <Save className="h-4 w-4 mr-1" />
                Save Draft
              </Button>
              <Button
                onClick={handleSubmitAppeal}
                disabled={isSubmitting || !appealContent}
              >
                <Send className="h-4 w-4 mr-1" />
                Mark as Submitted
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
